/**
 * Keyboard Navigation Hook
 * Provides keyboard shortcuts for chat navigation
 *
 * Shortcuts:
 * - Ctrl/Cmd + / : Focus search
 * - Ctrl/Cmd + N : New DM
 * - Ctrl/Cmd + Shift + N : New Group
 * - Ctrl/Cmd + [ : Previous conversation
 * - Ctrl/Cmd + ] : Next conversation
 * - Ctrl/Cmd + I : Toggle info panel
 * - Arrow keys : Navigate conversation list
 * - Enter/Space : Select conversation
 * - Escape : Close modals/panels
 */

const KeyboardNavigationHook = {
  mounted() {
    this.handleKeydown = this.handleKeydown.bind(this);
    document.addEventListener('keydown', this.handleKeydown);

    // Track current focus for restoration
    this.previousFocus = null;
  },

  destroyed() {
    document.removeEventListener('keydown', this.handleKeydown);
  },

  handleKeydown(e) {
    // Don't intercept when typing in inputs (except for escape)
    const isInputFocused = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName);

    if (e.key === 'Escape') {
      this.handleEscape(e);
      return;
    }

    if (isInputFocused && !e.ctrlKey && !e.metaKey) {
      return;
    }

    // Global shortcuts (Ctrl/Cmd + key)
    if (e.ctrlKey || e.metaKey) {
      switch(e.key.toLowerCase()) {
        case '/':
          e.preventDefault();
          this.focusSearch();
          break;
        case 'n':
          e.preventDefault();
          this.newConversation(e.shiftKey);
          break;
        case '[':
          e.preventDefault();
          this.navigateConversation(-1);
          break;
        case ']':
          e.preventDefault();
          this.navigateConversation(1);
          break;
        case 'i':
          e.preventDefault();
          this.toggleInfoPanel();
          break;
        case 'k':
          e.preventDefault();
          this.focusSearch();
          break;
      }
    }

    // Conversation list navigation (when sidebar focused)
    if (e.target.closest('[role="listbox"]') || e.target.closest('[data-conversation-list]')) {
      this.handleListNavigation(e);
    }
  },

  handleListNavigation(e) {
    const listbox = this.el.querySelector('[role="listbox"]') ||
                   this.el.querySelector('[data-conversation-list]');
    if (!listbox) return;

    const items = listbox.querySelectorAll('[role="option"], [data-conversation-item]');
    if (items.length === 0) return;

    const currentIndex = Array.from(items).findIndex(
      item => item.getAttribute('aria-selected') === 'true' ||
              item.classList.contains('bg-isx-drkgray')
    );

    switch(e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this.selectItem(items, Math.min(currentIndex + 1, items.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        this.selectItem(items, Math.max(currentIndex - 1, 0));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        const currentItem = items[currentIndex];
        if (currentItem) {
          const button = currentItem.querySelector('button') || currentItem;
          button.click();
        }
        break;
      case 'Home':
        e.preventDefault();
        this.selectItem(items, 0);
        break;
      case 'End':
        e.preventDefault();
        this.selectItem(items, items.length - 1);
        break;
    }
  },

  selectItem(items, index) {
    if (index < 0 || index >= items.length) return;

    items.forEach((item, i) => {
      item.setAttribute('aria-selected', i === index ? 'true' : 'false');
      const button = item.querySelector('button');
      if (button) {
        button.setAttribute('tabindex', i === index ? '0' : '-1');
      }
    });

    const selectedItem = items[index];
    selectedItem.scrollIntoView({ block: 'nearest', behavior: 'smooth' });

    const focusTarget = selectedItem.querySelector('button') || selectedItem;
    focusTarget.focus();
  },

  focusSearch() {
    const searchInput = this.el.querySelector('input[type="text"][placeholder*="Search"]') ||
                       this.el.querySelector('input[data-search]') ||
                       document.querySelector('input[placeholder*="Search conversations"]');
    if (searchInput) {
      this.previousFocus = document.activeElement;
      searchInput.focus();
      searchInput.select();
    }
  },

  newConversation(isGroup) {
    const event = isGroup ? 'open_group_modal' : 'open_dm_modal';
    this.pushEvent(event, {});
  },

  navigateConversation(direction) {
    const items = Array.from(
      this.el.querySelectorAll('[role="option"], [data-conversation-item]')
    );

    const currentIndex = items.findIndex(
      item => item.getAttribute('aria-selected') === 'true' ||
              item.classList.contains('bg-isx-drkgray')
    );

    const newIndex = Math.max(0, Math.min(items.length - 1, currentIndex + direction));

    if (items[newIndex]) {
      const button = items[newIndex].querySelector('button') || items[newIndex];
      button.click();
    }
  },

  toggleInfoPanel() {
    this.pushEvent('toggle_info_panel', {});
  },

  handleEscape(e) {
    // Close any open modals
    const modal = document.querySelector('[data-modal]');
    if (modal) {
      const closeButton = modal.querySelector('[phx-click*="close"]');
      if (closeButton) {
        closeButton.click();
        e.preventDefault();
        return;
      }
    }

    // Close info panel if open
    const infoPanel = document.querySelector('[data-info-panel]');
    if (infoPanel) {
      this.pushEvent('toggle_info_panel', {});
      e.preventDefault();
      return;
    }

    // Restore previous focus
    if (this.previousFocus && this.previousFocus.focus) {
      this.previousFocus.focus();
      this.previousFocus = null;
    }
  }
};

export default KeyboardNavigationHook;
